import type { ReactNode } from 'react';

import { cn } from '../lib/cn';
import { useTitle } from '../lib/use-title';

// One width per page, set here and never as a page's own max-w or margin: `full` fills what
// the sidebar leaves (lists, tables, canvas), `wide` and `narrow` are capped and centred.
// Unset keeps the old left-aligned 1280 column for pages not migrated yet.
export type TPageWidth = 'full' | 'wide' | 'narrow';

const PAGE_WIDTH: Record<TPageWidth | 'legacy', string> = {
  full: 'w-full',
  wide: 'mx-auto w-full max-w-page-wide',
  narrow: 'mx-auto w-full max-w-page-narrow',
  legacy: 'w-full max-w-content',
};

/** A row between the header and the body (tabs, filters) that lines up with both. */
export const PageBand = ({
  width,
  children,
  className,
}: {
  width: TPageWidth;
  children: ReactNode;
  className?: string;
}) => <div className={cn(PAGE_WIDTH[width], 'px-page', className)}>{children}</div>;

export const Page = ({
  children,
  className,
  width,
}: {
  children: ReactNode;
  className?: string;
  width?: TPageWidth;
}) => (
  <div className={cn('flex flex-col gap-section p-page', PAGE_WIDTH[width ?? 'legacy'], className)}>
    {children}
  </div>
);

interface IPageHeaderProps {
  title: string;
  badge?: ReactNode;
  description?: ReactNode;
  breadcrumbs?: ReactNode;
  actions?: ReactNode;
  width?: TPageWidth;
}

export const PageHeader = ({
  title,
  badge,
  description,
  breadcrumbs,
  actions,
  width = 'full',
}: IPageHeaderProps) => {
  useTitle(title);

  // The rule runs edge to edge; the text lines up with the body's column.
  return (
    <header className="border-b border-border-sunken">
      <div className={cn('flex items-start justify-between gap-6 p-page', PAGE_WIDTH[width])}>
        <div className="min-w-0">
          {breadcrumbs && <div className="mb-2 text-meta text-muted">{breadcrumbs}</div>}
          <div className="flex min-w-0 items-center gap-3">
            <h1 className="truncate text-page font-semibold tracking-page text-primary">{title}</h1>
            {badge && <span className="flex shrink-0 items-center">{badge}</span>}
          </div>
          {description && <p className="mt-1 text-data text-muted">{description}</p>}
        </div>
        {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
      </div>
    </header>
  );
};

interface ISectionProps {
  title?: string;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  id?: string;
}

export const Section = ({
  title,
  description,
  actions,
  children,
  className,
  id,
}: ISectionProps) => (
  <section id={id} className={cn('scroll-mt-page', className)}>
    {(title || actions) && (
      <div className="mb-section-gap flex items-start justify-between gap-4">
        <div>
          {title && <h2 className="text-section font-semibold text-primary">{title}</h2>}
          {description && <p className="mt-1 max-w-form text-data text-muted">{description}</p>}
        </div>
        {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
      </div>
    )}
    {children}
  </section>
);

export const Card = ({ children, className }: { children: ReactNode; className?: string }) => (
  <div
    className={cn('rounded-card border border-border-raised bg-surface-raised p-card', className)}
  >
    {children}
  </div>
);

export const DataPanel = ({ children, className }: { children: ReactNode; className?: string }) => (
  <div className={cn('rounded-control bg-surface-sunken p-input', className)}>{children}</div>
);

export const Banner = ({ children, detail }: { children: ReactNode; detail?: ReactNode }) => (
  <div className="rounded-control bg-danger-surface px-input py-3">
    <p className="text-data text-primary">{children}</p>
    {detail && <p className="mt-2 font-mono text-meta text-muted">{detail}</p>}
  </div>
);

export const StateLabel = ({ children }: { children: ReactNode }) => (
  <p className="mb-4 font-mono text-micro font-medium tracking-caps uppercase text-muted">
    {children}
  </p>
);

export const Rule = ({ children }: { children: ReactNode }) => (
  <div className="mt-section border-t border-border-sunken pt-5 text-data text-muted">
    {children}
  </div>
);
